import { useQueryClient } from '@tanstack/react-query';
import { isEqual } from 'lodash-es';
import { ChevronRight } from 'lucide-react';
import React, { FC, useCallback, useEffect, useRef, useState } from 'react';
import { FieldValues, FormProvider, SubmitHandler, useForm } from 'react-hook-form';
import { useLocation, useParams } from 'react-router-dom';

import {
  Button,
  Field,
  FieldContent,
  FieldDescription,
  FieldGroup,
  FieldLabel,
  Input,
  RadioGroup,
  RadioGroupItem,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@column/ui';

import { EntryClassCodeDefinitions } from '../TypeContent/ach';
import { PageActions } from '~/app/Layout/PageFrameContext';
import { ROUTE } from '~/app/routes';
import { Loading, RenderFields } from '~/components';
import { useDialog } from '~/components/Dialog/Provider';
import { useFormErrorHandler } from '~/hooks/useFormErrorHandler';
import {
  useCreateTransferTemplate,
  useGetTransferTemplate,
  useUpdateTransferTemplate,
} from '~/hooks/useTransferTemplates';
import { prefetchBankAccountsByIds, prefetchCounterpartiesByIds } from '~/lib/api';
import { BankAccountSelector } from '~/lib/components/selectors';
import { useNavigate } from '~/lib/navigation';
import { getTransferIcon } from '~/lib/transfers/utils';
import { TransferTemplateResponse, TransferTemplateType } from '~/repositories/Transfer/TransferTemplateRepository';
import { useNotificationStore } from '~/stores/Notification';
import { useSessionStore } from '~/stores/Session';
import { log, removeEmptyString } from '~/util';

import { EditTemplateTable } from './_partial/EditTemplateTable';
import { isACHTransferAuthorized } from './_utils/ach';
import {
  buildCreateTransferTemplateRequest,
  buildUpdateTransferTemplateRequest,
  validateTransfers,
} from './_utils/api';
import { isBookTransferAuthorized } from './_utils/book';
import { isWireTransferAuthorized } from './_utils/wire';
import { TransferTemplateForm } from './types';

interface Params {
  id: string;
}

interface LocationState {
  defaultFormValues?: TransferTemplateForm | null;
}

const TEMPLATE_TYPE_OPTIONS = [
  { value: TransferTemplateType.ACH, label: 'ACH' },
  { value: TransferTemplateType.Wire, label: 'Wire' },
  { value: TransferTemplateType.Book, label: 'Book' },
];

const ACH_TYPE_OPTIONS = [
  { value: 'DEBIT', label: 'Debit', description: 'Pull money from a counterparty to your bank account.' },
  { value: 'CREDIT', label: 'Credit', description: 'Push money from your bank account to a counterparty.' },
];

const ENTRY_CLASS_CODES = ['CCD', 'CIE', 'CTX', 'PPD', 'TEL', 'WEB'];

// Normalizes a template so a saved template and the live form compare equal: amounts are coerced to
// strings (the form holds them as strings) and the hydrated-only description fields are dropped.
const normalizeTemplateForComparison = (obj: Record<string, unknown>) => {
  const normalized = JSON.parse(JSON.stringify(obj));

  // visibleColumns is UI-only state derived from the template type (see EditTemplateTable); it is set on
  // the live form but never present on the fetched template, so it must not count as an edit.
  delete normalized.visibleColumns;

  const processTransfers = (
    key: string,
    transformFn: (transfer: Record<string, unknown>) => Record<string, unknown>
  ) => {
    if (normalized[key]) {
      normalized[key] = normalized[key].map(transformFn);
    }
  };

  processTransfers('wireTransfers', (transfer) => ({
    ...transfer,
    amount: String(transfer.amount),
    bankAccountDescription: undefined,
    counterpartyDescription: undefined,
  }));

  processTransfers('achTransfers', (transfer) => ({
    ...transfer,
    amount: String(transfer.amount),
    bankAccountDescription: undefined,
    counterpartyDescription: undefined,
  }));

  processTransfers('bookTransfers', (transfer) => ({
    ...transfer,
    amount: String(transfer.amount),
    senderBankAccountDescription: undefined,
    receiverBankAccountDescription: undefined,
  }));

  return normalized;
};

const getTemplateHasChanges = (
  initialValues: Partial<TransferTemplateResponse> | null,
  currentValues: TransferTemplateForm
): boolean => {
  const initial = normalizeTemplateForComparison(removeEmptyString<Record<string, unknown>>(initialValues ?? {}));
  const current = normalizeTemplateForComparison(removeEmptyString<Record<string, unknown>>(currentValues));

  return !isEqual(initial, current);
};

interface ReviewButtonProps {
  isSendable: boolean;
  isDisabled: boolean;
  onClick: () => void;
}

const ReviewButton: FC<ReviewButtonProps> = ({ isSendable, isDisabled, onClick }) => {
  const button = (
    <Button type="button" size="sm" disabled={!isSendable || isDisabled} onClick={onClick}>
      Review
      <ChevronRight />
    </Button>
  );

  if (isSendable) {
    return button;
  }

  // Disabled buttons don't fire pointer events, so the tooltip hangs off a wrapper.
  return (
    <TooltipProvider>
      <Tooltip>
        <TooltipTrigger asChild>
          <span className="inline-flex" tabIndex={0}>
            {button}
          </span>
        </TooltipTrigger>
        <TooltipContent side="left">You do not have permission to send this template.</TooltipContent>
      </Tooltip>
    </TooltipProvider>
  );
};

export const PageTransferTemplatesEdit: FC = () => {
  const { id } = useParams<keyof Params>() as Params;
  const { openAlert } = useDialog();
  const { addSuccessNotification, addDangerNotification } = useNotificationStore();
  const { isAuthorized } = useSessionStore();
  const navigate = useNavigate();
  const { handleFormErrors } = useFormErrorHandler();

  const location = useLocation();
  const locationState = location.state as LocationState;

  const initialValues = useRef<Partial<TransferTemplateResponse> | null>(null);

  const { createRequest: getTransferTemplate, isLoading: isLoadingGetTransferTemplate } = useGetTransferTemplate({
    onError: (error) => {
      addDangerNotification({
        content: error.message,
      });
      navigate(`${ROUTE.TRANSFERS}/templates`);
    },
  });

  const { createRequest: createTransferTemplate } = useCreateTransferTemplate({
    onError: (error) => {
      addDangerNotification({
        content: error.message,
      });
    },
    onSuccess: () => {
      addSuccessNotification({
        content: 'Transfer template created successfully',
      });
    },
  });

  const { createRequest: updateTransferTemplate } = useUpdateTransferTemplate({
    onError: (error) => {
      addDangerNotification({
        content: error.message,
      });
    },
    onSuccess: () => {
      addSuccessNotification({
        content: 'Transfer template updated successfully',
      });
    },
  });

  const defaultFormValues: Partial<TransferTemplateForm> = locationState?.defaultFormValues ?? {
    templateType: TransferTemplateType.ACH,
    isPartialAccess: false,
    achDefaultParameters: {},
    achTransfers: [],
    wireDefaultParameters: {},
    wireTransfers: [],
    bookDefaultParameters: {},
    visibleColumns: ['amount', 'counterpartyId', 'type', 'bankAccountId', 'description', 'entryClassCode'],
  };

  const methods = useForm<TransferTemplateForm>({
    mode: 'onSubmit',
    defaultValues: defaultFormValues,
  });

  const { handleSubmit, reset, watch, getValues } = methods;
  const templateType = watch('templateType');
  const achTransfers = watch('achTransfers');
  const wireTransfers = watch('wireTransfers');
  const bookTransfers = watch('bookTransfers');
  const isTemplateSendable =
    (achTransfers ?? []).every((transfer) => isACHTransferAuthorized(transfer, isAuthorized)) &&
    (wireTransfers ?? []).every((transfer) => isWireTransferAuthorized(transfer, isAuthorized)) &&
    (bookTransfers ?? []).every((transfer) => isBookTransferAuthorized(transfer, isAuthorized));

  const queryClient = useQueryClient();
  const [isHydratingTemplate, setIsHydratingTemplate] = useState<boolean>(!!id && !locationState?.defaultFormValues);

  useEffect(() => {
    if (id && !locationState?.defaultFormValues) {
      setIsHydratingTemplate(true);
      getTransferTemplate({ transferTemplateId: id })
        .then(async (data) => {
          if (!data) {
            return;
          }

          const counterpartyIds = [...(data.achTransfers ?? []), ...(data.wireTransfers ?? [])]
            .map((t) => t.counterpartyId)
            .filter((cpId): cpId is string => Boolean(cpId));
          const bankAccountIds = [
            ...(data.achTransfers ?? []).map((t) => t.bankAccountId),
            ...(data.wireTransfers ?? []).map((t) => t.bankAccountId),
            ...(data.bookTransfers ?? []).flatMap((t) => [t.senderBankAccountId, t.receiverBankAccountId]),
          ].filter((baId): baId is string => Boolean(baId));
          await Promise.all([
            prefetchCounterpartiesByIds(queryClient, counterpartyIds),
            prefetchBankAccountsByIds(queryClient, bankAccountIds),
          ]);

          const newTransferTemplate = { ...data } as Partial<TransferTemplateResponse>;
          delete newTransferTemplate?.version;

          reset(newTransferTemplate);
          initialValues.current = newTransferTemplate;
        })
        .finally(() => setIsHydratingTemplate(false));
    }

    if (locationState?.defaultFormValues) {
      reset(locationState.defaultFormValues);
      initialValues.current = locationState.defaultFormValues;
    }
  }, [id, locationState?.defaultFormValues]);

  const onSuccess = useCallback(
    async (transferTemplate: TransferTemplateForm) => {
      // Validate the transfer template
      const validation = validateTransfers(transferTemplate.templateType, transferTemplate);
      if (!validation.isValid) {
        validation.errors.forEach((error) => {
          addDangerNotification({ content: error.message });
        });
        return;
      }

      if (id) {
        // Update existing template
        const updateRequest = buildUpdateTransferTemplateRequest(transferTemplate, id);
        const newData = await updateTransferTemplate(updateRequest);

        if (newData) {
          reset(newData);
          // Re-baseline so Save goes inactive again now that the edits are persisted.
          initialValues.current = newData;
        }

        return;
      }

      try {
        // Create new template
        const createRequest = buildCreateTransferTemplateRequest(transferTemplate);
        const newData = await createTransferTemplate(createRequest);

        navigate(`${ROUTE.TRANSFERS}/templates/edit/${newData?.transferTemplateId}`, { replace: true });
      } catch (error) {
        addDangerNotification({
          content: error instanceof Error ? error.message : 'An unknown error occurred',
        });
      }
    },
    [id, addDangerNotification, updateTransferTemplate, reset, isAuthorized, createTransferTemplate, navigate]
  );

  const onError: SubmitHandler<FieldValues> = useCallback((errors) => {
    handleFormErrors({ errors });
  }, []);

  const handleCancelClick = useCallback(() => {
    reset();
    navigate(`${ROUTE.TRANSFERS}/templates`, { state: { defaultFormValues: null } });
  }, []);

  const handleReviewClick = () => {
    const hasChanges = getTemplateHasChanges(initialValues.current, watch());

    log({ type: 'info', name: 'ReviewTransferTemplate', context: { hasChanges } });

    if (!hasChanges) {
      navigate(`${ROUTE.TRANSFERS}/templates/review/${id}`, {
        state: { defaultFormValues: watch() },
      });
      return;
    }

    openAlert({
      headline: 'Review Transfer Template',
      content: 'Review the transfer template and make any necessary changes.',
      confirmButton: {
        label: 'Continue',
        variant: 'primary',
        fullWidth: true,
      },
      cancelButton: {
        label: 'Save & Continue',
        variant: 'secondary',
        fullWidth: true,
      },
      icon: 'CircleQuestionmark',
      type: 'primary',
      onSuccess: () => {
        navigate(`${ROUTE.TRANSFERS}/templates/review/${id}`, {
          state: { defaultFormValues: watch() },
        });
      },
      onClose: () => {
        onSuccess(getValues()).then(() => {
          navigate(`${ROUTE.TRANSFERS}/templates/review/${id}`, { state: { defaultFormValues: null } });
        });
      },
    });
  };

  const isLoading = (isLoadingGetTransferTemplate || isHydratingTemplate) && !!id && !locationState?.defaultFormValues;

  // When editing an existing template, keep Save inactive until the form differs from the saved values.
  const isSaveDisabled = isLoading || (!!id && !getTemplateHasChanges(initialValues.current, watch()));

  // One span, so the button's flex gap doesn't add space before " Template".
  const submitLabel = (
    <span>
      {id ? 'Save' : 'Create'}
      <span className="max-[600px]:hidden"> Template</span>
    </span>
  );

  return (
    <FormProvider {...methods}>
      <form onSubmit={handleSubmit(onSuccess, onError)}>
        <PageActions>
          <Button type="button" variant="secondary" size="sm" onClick={handleCancelClick}>
            Cancel
          </Button>
          <Button type="button" size="sm" onClick={handleSubmit(onSuccess, onError)} disabled={isSaveDisabled}>
            {submitLabel}
          </Button>
          {id && <ReviewButton isSendable={isTemplateSendable} isDisabled={isLoading} onClick={handleReviewClick} />}
        </PageActions>

        <Loading isLoading={isLoading}>
          <div className="pb-6">
            {RenderFields<TransferTemplateResponse>({
              sections: [
                {
                  fields: [
                    {
                      id: 'description',
                      label: 'Template Name',
                      defaultValue: '',
                      rules: {
                        required: true,
                      },
                      children: ({ value, onChange, onBlur }, { error }) => (
                        <Input
                          placeholder="Template Name"
                          aria-invalid={!!error}
                          value={String(value ?? '')}
                          onChange={onChange}
                          onBlur={onBlur}
                        />
                      ),
                    },
                    {
                      id: 'templateType',
                      label: 'Template Type',
                      defaultValue: TransferTemplateType.ACH,
                      children: ({ value, onChange }) => (
                        // The type is fixed once a template exists, so only the chosen one is shown, with its transfer icon.
                        <RadioGroup value={String(value ?? '')} onValueChange={onChange} disabled={!!id}>
                          <FieldGroup variant="outline" orientation="responsive">
                            {TEMPLATE_TYPE_OPTIONS.filter((option) => !id || option.value === value).map((option) => (
                              <Field key={option.value} orientation="horizontal">
                                {id ? (
                                  <span className="flex shrink-0 text-primary dark:text-blue-500 [&>svg]:[--icon-color:currentColor] [&>svg]:[--icon-size-x:16px] [&>svg]:[--icon-size-y:16px] [&>svg]:[--icon-size:16px]">
                                    {getTransferIcon(option.value)}
                                  </span>
                                ) : (
                                  <RadioGroupItem value={option.value} id={`template-type-${option.value}`} />
                                )}
                                <FieldContent>
                                  <FieldLabel htmlFor={`template-type-${option.value}`}>{option.label}</FieldLabel>
                                </FieldContent>
                              </Field>
                            ))}
                          </FieldGroup>
                        </RadioGroup>
                      ),
                    },
                  ],
                },
                {
                  headline: (
                    <>
                      Default Parameters <small>(Optional)</small>
                    </>
                  ),
                  description: 'These parameters apply to any new transfers added to the transfers list below',
                  fields: [
                    {
                      hide: templateType !== TransferTemplateType.ACH,
                      id: 'achDefaultParameters.bankAccountId',
                      defaultValue: '',
                      label: 'Bank Account',
                      description: 'Select a bank account to originate the ACH transfer.',
                      children: ({ value, onChange, onBlur }, { error }) => (
                        <BankAccountSelector
                          value={(value as string) || ''}
                          onChange={onChange}
                          onBlur={onBlur}
                          hasError={!!error}
                        />
                      ),
                    },
                    {
                      hide: templateType !== TransferTemplateType.Wire,
                      id: 'wireDefaultParameters.bankAccountId',
                      defaultValue: '',
                      label: 'Bank Account',
                      description: 'Select a bank account to originate the Wire transfer.',
                      children: ({ value, onChange, onBlur }, { error }) => (
                        <BankAccountSelector
                          value={(value as string) || ''}
                          onChange={onChange}
                          onBlur={onBlur}
                          hasError={!!error}
                        />
                      ),
                    },
                    {
                      hide: templateType !== TransferTemplateType.Book,
                      id: 'bookDefaultParameters.senderBankAccountId',
                      defaultValue: '',
                      label: 'Sender Bank Account',
                      description: 'Select a bank account to send the book transfer from.',
                      children: ({ value, onChange, onBlur }, { error }) => (
                        <BankAccountSelector
                          value={(value as string) || ''}
                          onChange={onChange}
                          onBlur={onBlur}
                          hasError={!!error}
                        />
                      ),
                    },
                    {
                      hide: templateType !== TransferTemplateType.Book,
                      id: 'bookDefaultParameters.receiverBankAccountId',
                      defaultValue: '',
                      label: 'Receiver Bank Account',
                      description: 'Select a bank account to send the book transfer to.',
                      children: ({ value, onChange, onBlur }, { error }) => (
                        <BankAccountSelector
                          value={(value as string) || ''}
                          onChange={onChange}
                          onBlur={onBlur}
                          hasError={!!error}
                        />
                      ),
                    },
                    {
                      id: 'achDefaultParameters.type',
                      hide: templateType !== TransferTemplateType.ACH,
                      label: 'Type',
                      description: 'Select between ACH Debit or ACH Credit.',
                      defaultValue: 'DEBIT',
                      children: ({ value, onChange }) => (
                        <RadioGroup value={String(value ?? '')} onValueChange={onChange}>
                          <FieldGroup variant="outline" orientation="responsive">
                            {ACH_TYPE_OPTIONS.map((option) => (
                              <Field key={option.value} orientation="horizontal">
                                <RadioGroupItem value={option.value} id={`template-ach-type-${option.value}`} />
                                <FieldContent>
                                  <FieldLabel htmlFor={`template-ach-type-${option.value}`}>{option.label}</FieldLabel>
                                  <FieldDescription className="text-xs text-pretty">
                                    {option.description}
                                  </FieldDescription>
                                </FieldContent>
                              </Field>
                            ))}
                          </FieldGroup>
                        </RadioGroup>
                      ),
                    },
                    {
                      id: 'achDefaultParameters.entryClassCode',
                      hide: templateType !== TransferTemplateType.ACH,
                      label: 'Entry Class Code',
                      defaultValue: 'CCD',
                      tooltip: {
                        headline: 'Entry Class Code Definitions',
                        content: EntryClassCodeDefinitions,
                      },
                      children: ({ value, onChange, onBlur }, { error }) => (
                        <Select
                          value={String(value ?? '')}
                          onValueChange={onChange}
                          onOpenChange={(open) => !open && onBlur()}
                        >
                          <SelectTrigger size="lg" className="w-full" aria-invalid={!!error}>
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            {ENTRY_CLASS_CODES.map((code) => (
                              <SelectItem key={code} value={code} data-cy={`dropdown-option-${code}`}>
                                {code}
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      ),
                    },
                    {
                      id: 'wireDefaultParameters.description',
                      hide: templateType !== TransferTemplateType.Wire,
                      label: 'Description',
                      defaultValue: '',
                      children: ({ value, onChange, onBlur }, { error }) => (
                        <Input
                          placeholder="Description"
                          aria-invalid={!!error}
                          value={String(value ?? '')}
                          onChange={onChange}
                          onBlur={onBlur}
                        />
                      ),
                    },
                    {
                      id: 'bookDefaultParameters.description',
                      hide: templateType !== TransferTemplateType.Book,
                      label: 'Description',
                      defaultValue: '',
                      children: ({ value, onChange, onBlur }, { error }) => (
                        <Input
                          placeholder="Description"
                          aria-invalid={!!error}
                          value={String(value ?? '')}
                          onChange={onChange}
                          onBlur={onBlur}
                        />
                      ),
                    },
                  ],
                },
                {
                  headline: 'Transfers',
                  description:
                    'Create a list of default transfers. Parameters can be added to or edited when the template is in use.',
                  content: !isLoading ? <EditTemplateTable type={templateType} /> : null,
                },
              ],
            })}
            <div className="flex justify-between px-(--page-margin)">
              <Button type="button" variant="secondary" size="sm" onClick={handleCancelClick}>
                Cancel
              </Button>
              <div className="flex gap-2">
                <Button type="submit" size="sm" disabled={isSaveDisabled}>
                  {submitLabel}
                </Button>
                {id && (
                  <ReviewButton isSendable={isTemplateSendable} isDisabled={isLoading} onClick={handleReviewClick} />
                )}
              </div>
            </div>
          </div>
        </Loading>
      </form>
    </FormProvider>
  );
};
